<template>
  <section
    id="plan-actions"
    :style="{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }">
    <a-row :gutter="{xl: 200, md: 80, sm: 20, xs: 8}">
      <a-col>
        <a-row
          v-if="showButtons"
          :gutter="8">
          <a-col>
            <a-button
              v-if="canResetStep('furniture')"
              :disabled="[undefined, 2, -1].includes(planStatusFurniture)"
              :style="{ marginBottom: '.5rem' }"
              @click="resetFromStep('furniture')">
              Сброс Фурнитурки
            </a-button>
          </a-col>
          <a-col>
            <a-button
              v-if="canResetStep('unreal')"
              :disabled="[undefined, 2, -1].includes(planStatusUnreal)"
              :style="{ marginBottom: '.5rem' }"
              @click="resetFromStep('unreal')">
              Сброс Анрила
            </a-button>
          </a-col>
          <a-col v-if="canResetStep('gltf') && planStatusGltf !== undefined && ![2, -1].includes(planStatusGltf)">
            <a-button
              :style="{ marginBottom: '.5rem' }"
              @click="resetFromStep('gltf')">
              Сброс GLTF
            </a-button>
          </a-col>
        </a-row>
      </a-col>

      <a-col :style="{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }">
        <a-dropdown v-if="buttons.length >= 5">
          <template #overlay>
            <a-menu @click="onDropdownButtonClick">
              <a-menu-item
                v-for="item in buttons"
                :key="item.action">
                {{ item.text }} ({{ item.action }})
              </a-menu-item>
            </a-menu>
          </template>
          <a-button>
            Действия
            <down-outlined />
          </a-button>
        </a-dropdown>

        <a-button
          v-for="(item) in buttons"
          v-else
          :key="item.action"
          :type="item.type"
          size="medium"
          @click="onButtonClick(item.action)">
          {{ item.text }}
        </a-button>
      </a-col>
    </a-row>
    <a-modal
      v-model:open="isModalVisible"
      title="Подтверждение параметров проверки"
      :confirm-loading="confirmLoading"
      ok-text="Подтвердить"
      cancel-text="Отмена"
      centered
      @ok="handleModalOk"
      @cancel="handleModalCancel">
      <a-form layout="vertical">
        <a-form-item label="Чеклисты">
          <a-select
            v-model:value="selectedChecklists"
            mode="multiple"
            :options="checklistOptions"
            placeholder="Выберите чеклисты..."
            style="width: 100%" />
        </a-form-item>

        <a-form-item
          label="Своя инструкция">
          <a-textarea
            v-model:value="customChecklist"
            placeholder="Введите ваши дополнительные инструкции..."
            :rows="4"
            show-count
            :maxlength="1000" />
        </a-form-item>
      </a-form>
    </a-modal>
  </section>
</template>

<script>
import { DownOutlined } from '@ant-design/icons-vue';
import { api } from '@/api';
import { StepStatus } from '@/enums/StepStatus';
import { PlanStatus } from '@/enums/planStatus';
import { computed, ref, watch } from 'vue';
import { message } from 'ant-design-vue';

export default {
  components: {
    DownOutlined,
  },
  props: {
    crmPlanId: {
      type: Number,
      required: true,
    },
    planType: {
      type: Number,
      required: true,
    },
    buttons: {
      type: Array,
      required: true,
    },
    showButtons: {
      type: Boolean,
      required: true,
    },
    planId: {
      type: String,
      required: true,
    },
    planStatusFurniture: {
      type: Number,
      required: true,
    },
    planStatusUnreal: {
      type: Number,
      required: true,
    },
    planStatusGltf: {
      type: Number,
      default: undefined,
    },
    allowedResetSteps: {
      type: Array,
      default: () => ['furniture', 'unreal', 'gltf'],
    },
  },
  emits: ['buttonClick', 'modalSuccess'],
  setup(props, { emit }) {
    const isModalVisible = ref(false);
    const currentAction = ref(null);
    const confirmLoading = ref(false);

    const checklists = ref([]);
    const selectedChecklists = ref([]);
    const customChecklist = ref('');

    const checklistOptions = computed(() => checklists.value.map((item) => ({
      label: item.name,
      value: item.id,
    })));

    const loadChecklists = async () => {
      try {
        const response = await api.getCrmChecklists(props.crmPlanId);
        const { data } = response;
        checklists.value = [
          ...data.default_checklists,
          ...data.available_checklists,
        ];
        selectedChecklists.value = data.default_checklists.map((item) => item.id);
      } catch (error) {
        message.error('Не удалось загрузить список чеклистов');
      }
    };

    const actionRequiresModal = (action) => action.includes(PlanStatus.AwaitingLlmVerification);
    const canResetStep = (step) => props.allowedResetSteps.includes(step);

    const onButtonClick = (action) => {
      if (actionRequiresModal(action)) {
        currentAction.value = action;
        isModalVisible.value = true;
      } else {
        emit('buttonClick', action);
      }
    };

    const onDropdownButtonClick = ({ key: action }) => {
      if (actionRequiresModal(action)) {
        currentAction.value = action;
        isModalVisible.value = true;
      } else {
        emit('buttonClick', action);
      }
    };

    const handleModalOk = async () => {
      confirmLoading.value = true;
      try {
        const requestData = {
          planId: props.crmPlanId,
          type: props.planType,
          checklists: selectedChecklists.value,
          custom_checklist: customChecklist.value.trim(),
        };

        await api.reviewCrmPlan(requestData);
        message.success('Проверка запущена');
        emit('modalSuccess');
      } catch (error) {
        if (error?.response?.status === 403) {
          const messageText = error?.response?.data?.message || 'Доступ запрещен';
          message.error(messageText);
        } else {
          message.error('Произошла ошибка при отправке данных');
        }
      }

      isModalVisible.value = false;
      currentAction.value = null;
      selectedChecklists.value = [];
      customChecklist.value = '';
      confirmLoading.value = false;
    };

    const handleModalCancel = () => {
      isModalVisible.value = false;
      currentAction.value = null;
      selectedChecklists.value = [];
      customChecklist.value = '';
      confirmLoading.value = false;
    };

    watch(isModalVisible, (newVal) => {
      loadChecklists();
    });

    const resetFromStep = async (step) => {
      await api.resetFromStep(props.planId, { step });
      props.planStatusUnreal = StepStatus.ERROR;
      location.reload();
    };

    return {
      onButtonClick,
      onDropdownButtonClick,
      resetFromStep,
      canResetStep,
      isModalVisible,
      currentAction,
      confirmLoading,
      checklists,
      selectedChecklists,
      customChecklist,
      checklistOptions,
      handleModalOk,
      handleModalCancel,
    };
  },
};
</script>
